import { useState, useRef, useEffect } from "react";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { Mail, MessageSquare } from "lucide-react";
import { BrandInput } from "@/components/ui/BrandInput";
import { saveEmail, savePhoneNumber, n8nSaveEmail, n8nSavePhone, n8nSaveEmailSS, n8nSavePhoneSS, n8nSavePhoneSlickText } from "@/lib/firebase";
import { validateEmail, validatePhone } from "@/lib/validation";
import { canSubmit, recordSubmission, getSecondsRemaining } from "@/lib/rate-limit";
import { PHOTOS, getMobileUrl } from "@/lib/photos";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";

const photos = {
  photo1: getMobileUrl(PHOTOS.GROUP.fiveFriendsRoostertail),
  photo2: getMobileUrl(PHOTOS.DANCING.crowdHandsUpCaptainHats),
  photo3: getMobileUrl(PHOTOS.FEMALE_ONLY.fourWomenRoostertail),
  photo4: getMobileUrl(PHOTOS.CROWD.audienceHandsRaised),
};

const StayInTheKnow = () => {
  const ref = useRef<HTMLElement>(null);
  const isInView = useMobileOnceInView(ref, { margin: "-100px" });
  const [mode, setMode] = useState<"email" | "sms" | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState(false);
  const [hasSubmittedPhone, setHasSubmittedPhone] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    const emailSubmitted = localStorage.getItem("yrd_email_submitted") === "true";
    const phoneSubmitted = localStorage.getItem("yrd_phone_submitted") === "true";
    setHasSubmittedEmail(emailSubmitted);
    setHasSubmittedPhone(phoneSubmitted);
  }, []);

  // Cursor tracking for sway effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth spring physics for natural movement
  const springConfig = { damping: 25, stiffness: 150 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  // Transform cursor position to subtle movement ranges
  const photo1X = useTransform(smoothX, [-1, 1], [-15, 15]);
  const photo1Y = useTransform(smoothY, [-1, 1], [-10, 10]);
  const photo2X = useTransform(smoothX, [-1, 1], [12, -12]);
  const photo2Y = useTransform(smoothY, [-1, 1], [8, -8]);
  const photo3X = useTransform(smoothX, [-1, 1], [-10, 10]);
  const photo3Y = useTransform(smoothY, [-1, 1], [12, -12]);
  const photo4X = useTransform(smoothX, [-1, 1], [8, -8]);
  const photo4Y = useTransform(smoothY, [-1, 1], [-15, 15]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      // Normalize to -1 to 1 range
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      mouseX.set(x);
      mouseY.set(y);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue) return;

    const rateKey = mode === "email" ? "email" : "phone";
    if (!canSubmit(rateKey)) {
      setErrorMessage(`Please wait ${getSecondsRemaining(rateKey)}s before submitting again.`);
      return;
    }

    const validationError = mode === "email" ? validateEmail(inputValue) : validatePhone(inputValue);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage(null);
    setErrorMessage(null);
    
    try {
      if (mode === "email") {
        await Promise.all([
          saveEmail(inputValue, "stay-in-know"),
          n8nSaveEmail(inputValue, "stay-in-know").catch(console.error),
          n8nSaveEmailSS(inputValue, "stay-in-know").catch(console.error),
        ]);
        localStorage.setItem("yrd_email_submitted", "true");
        recordSubmission("email");
        setHasSubmittedEmail(true);
        
        if (hasSubmittedPhone) {
          setSuccessMessage("You're all set! Thanks for signing up.");
          setMode(null);
        } else {
          setSuccessMessage("Email submitted! Now, what's your phone number?");
          setMode("sms");
        }
        setInputValue("");
      } else {
        await Promise.all([
          savePhoneNumber(inputValue, "stay-in-know"),
          n8nSavePhone(inputValue, "stay-in-know").catch(console.error),
          n8nSavePhoneSS(inputValue, "stay-in-know").catch(console.error),
          n8nSavePhoneSlickText(inputValue).catch(console.error),
        ]);
        localStorage.setItem("yrd_phone_submitted", "true");
        recordSubmission("phone");
        setHasSubmittedPhone(true);
        
        if (hasSubmittedEmail) {
          setSuccessMessage("You're all set! Thanks for signing up.");
          setMode(null);
        } else {
          setSuccessMessage("Phone submitted! Now, what's your email?");
          setMode("email");
        }
        setInputValue("");
      }
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      ref={ref}
      id="info"
      className="relative py-28 md:py-40 px-6 overflow-hidden"
      style={{ backgroundColor: "#f2dc05" }}
    >
      {/* Noise texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />
      {/* Decorative photos with cursor sway - hidden on mobile */}
      {/* Top left photo */}
      <motion.div
        className="hidden md:block absolute top-12 left-8 lg:left-16 w-40 lg:w-48 h-52 lg:h-60 rounded-xl overflow-hidden shadow-2xl z-10"
        style={{ x: photo1X, y: photo1Y, rotate: -8 }}
        initial={{ opacity: 0, x: -50 }}
        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <img
          src={photos.photo1}
          alt="Yacht Rock vibes"
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      </motion.div>

      {/* Bottom right photo */}
      <motion.div
        className="hidden md:block absolute bottom-12 right-8 lg:right-16 w-40 lg:w-48 h-52 lg:h-60 rounded-xl overflow-hidden shadow-2xl z-10"
        style={{ x: photo2X, y: photo2Y, rotate: 6 }}
        initial={{ opacity: 0, x: 50 }}
        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        <img
          src={photos.photo2}
          alt="Yacht Rock crew"
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      </motion.div>

      {/* Top right photo (new) */}
      <motion.div
        className="hidden lg:block absolute top-20 right-12 xl:right-24 w-36 h-44 rounded-xl overflow-hidden shadow-2xl z-10"
        style={{ x: photo3X, y: photo3Y, rotate: 10 }}
        initial={{ opacity: 0, y: -30 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }}
        transition={{ duration: 0.8, delay: 0.7 }}
      >
        <img
          src={photos.photo3}
          alt="Festival moment"
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      </motion.div>

      {/* Bottom left photo (new) */}
      <motion.div
        className="hidden lg:block absolute bottom-20 left-12 xl:left-24 w-36 h-44 rounded-xl overflow-hidden shadow-2xl z-10"
        style={{ x: photo4X, y: photo4Y, rotate: -5 }}
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.8, delay: 0.9 }}
      >
        <img
          src={photos.photo4}
          alt="Festival vibes"
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      </motion.div>

      {/* Main content */}
      <div className="relative z-20 max-w-3xl mx-auto text-center">
        {/* Big headline */}
        <motion.h2
          className="text-5xl md:text-7xl lg:text-8xl font-bold text-yacht-charcoal mb-4 leading-tight"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Stay in the Know
        </motion.h2>

        <motion.p
          className="text-lg md:text-xl text-yacht-charcoal/70 mb-10 max-w-lg mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          Lineup news, ticket updates, and smooth vibes. Stay in the loop.
        </motion.p>

        {/* Dual buttons or form */}
        {!mode ? (
          hasSubmittedEmail && hasSubmittedPhone ? (
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <p className="text-green-700 font-semibold text-lg">You're already signed up! We'll keep you posted.</p>
            </motion.div>
          ) : (
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              {!hasSubmittedEmail && (
                <button
                  onClick={() => setMode("email")}
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-yacht-charcoal text-white font-semibold text-lg rounded-full hover:bg-yacht-charcoal/90 transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  <Mail className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  Sign up via Email
                </button>
              )}
              {!hasSubmittedPhone && (
                <button
                  onClick={() => setMode("sms")}
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-yacht-blue text-white font-semibold text-lg rounded-full hover:bg-yacht-blue/90 transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  <MessageSquare className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  Sign up via SMS
                </button>
              )}
            </motion.div>
          )
        ) : (
          <motion.form
            onSubmit={handleSubmit}
            className="max-w-md mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <BrandInput
                  type={mode === "email" ? "email" : "tel"}
                  id={mode === "email" ? "sitk-email" : "sitk-phone"}
                  name={mode === "email" ? "email" : "phone"}
                  autoComplete={mode === "email" ? "email" : "tel"}
                  inputMode={mode === "email" ? "email" : "tel"}
                  placeholder={
                    mode === "email" ? "your@email.com" : "Your phone number"
                  }
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  required
                  className="text-lg py-4 shadow-lg"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-4 bg-yacht-charcoal text-white font-semibold text-lg rounded-md hover:bg-yacht-charcoal/90 transition-all duration-300 disabled:opacity-50 shadow-lg"
              >
                {isSubmitting ? "..." : "Submit"}
              </button>
            </div>
            <div className="mt-4 flex flex-col items-center gap-2">
              {errorMessage && (
                <p className="text-red-600 text-sm font-medium">{errorMessage}</p>
              )}
              {successMessage && (
                <p className="text-green-600 font-medium">{successMessage}</p>
              )}
              {/* Only show switch button if the other method hasn't been submitted */}
              {mode === "email" && !hasSubmittedPhone && (
                <button
                  type="button"
                  onClick={() => {
                    setMode("sms");
                    setInputValue("");
                    setSuccessMessage(null);
                  }}
                  className="text-yacht-charcoal/60 hover:text-yacht-charcoal underline text-sm transition-colors"
                >
                  Switch to SMS →
                </button>
              )}
              {mode === "sms" && !hasSubmittedEmail && (
                <button
                  type="button"
                  onClick={() => {
                    setMode("email");
                    setInputValue("");
                    setSuccessMessage(null);
                  }}
                  className="text-yacht-charcoal/60 hover:text-yacht-charcoal underline text-sm transition-colors"
                >
                  ← Switch to Email
                </button>
              )}
            </div>
          </motion.form>
        )}
      </div>
    </section>
  );
};

export default StayInTheKnow;
